import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react';

interface AuthContextProps {
  children: ReactNode;
}

export const AuthContext = createContext<{
  role: 'franchisor' | 'consultant' | 'administrator' | '';
  setRole: Dispatch<
    SetStateAction<'' | 'franchisor' | 'consultant' | 'administrator'>
  >;
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
  password: string;
  setPassword: Dispatch<SetStateAction<string>>;
  firstName: string;
  setFirstName: Dispatch<SetStateAction<string>>;
  lastName: string;
  setLastName: Dispatch<SetStateAction<string>>;
  bio: string;
  setBio: Dispatch<SetStateAction<string>>;
  position: string;
  setPosition: Dispatch<SetStateAction<string>>;
  experience: string;
  setExperience: Dispatch<SetStateAction<string>>;
  rate: string;
  setRate: Dispatch<SetStateAction<string>>;
  consultantGroups: string[];
  setConsultantGroups: Dispatch<SetStateAction<string[]>>;
  brandname: string;
  setBrandName: Dispatch<SetStateAction<string>>;
  group: string[];
  setGroup: Dispatch<SetStateAction<string[]>>;
  country: string;
  setCountry: Dispatch<SetStateAction<string>>;
  city: string;
  setCity: Dispatch<SetStateAction<string>>;
  revenue: string;
  setRevenue: Dispatch<SetStateAction<string>>;
  investment: string;
  setInvestment: Dispatch<SetStateAction<string>>;
  company: string;
  setCompany: Dispatch<SetStateAction<string>>;
  profilePhoto: string;
  setProfilePhoto: Dispatch<SetStateAction<string>>;
  averageRevenue: number;
  setAverageRevenue: Dispatch<SetStateAction<number>>;
  minInitialInvestment: number;
  setMinInitialInvestment: Dispatch<SetStateAction<number>>;
  maxInitialInvestment: number;
  setMaxInitialInvestment: Dispatch<SetStateAction<number>>;
}>({
  role: '',
  setRole: () => null,
  email: '',
  setEmail: () => null,
  password: '',
  setPassword: () => null,
  firstName: '',
  setFirstName: () => null,
  lastName: '',
  setLastName: () => null,
  bio: '',
  setBio: () => null,
  position: '',
  setPosition: () => null,
  experience: '',
  setExperience: () => null,
  rate: '',
  setRate: () => null,
  consultantGroups: [' '],
  setConsultantGroups: () => null,
  brandname: '',
  setBrandName: () => null,
  group: [''],
  setGroup: () => null,
  country: '',
  setCountry: () => null,
  city: '',
  setCity: () => null,
  revenue: '',
  setRevenue: () => null,
  investment: '',
  setInvestment: () => null,
  company: '',
  setCompany: () => null,
  profilePhoto: '',
  setProfilePhoto: () => null,
  maxInitialInvestment: 0,
  minInitialInvestment: 0,
  averageRevenue: 1000,
  setAverageRevenue: () => null,
  setMaxInitialInvestment: () => null,
  setMinInitialInvestment: () => null,
});

const AuthContextProvider: FC<AuthContextProps> = ({ children }) => {
  const [role, setRole] = useState<
    '' | 'franchisor' | 'consultant' | 'administrator'
  >('franchisor');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [bio, setBio] = useState('');
  const [position, setPosition] = useState<string>('');
  const [experience, setExperience] = useState<string>('');
  const [rate, setRate] = useState<string>('');
  const [consultantGroups, setConsultantGroups] = useState<string[]>([]);
  const [brandname, setBrandName] = useState<string>('');
  const [group, setGroup] = useState<string[]>([]);
  const [country, setCountry] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [revenue, setRevenue] = useState<string>('');
  const [investment, setInvestment] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [averageRevenue, setAverageRevenue] = useState<number>(10);
  const [minInitialInvestment, setMinInitialInvestment] = useState<number>(0);
  const [maxInitialInvestment, setMaxInitialInvestment] =
    useState<number>(1000);
  const [profilePhoto, setProfilePhoto] = useState<string>(
    'https://fch-dev-01.s3.us-east-1.amazonaws.com/uploads/fch/8aebc924-e5a2-4eef-a556-b7a006a93ab1-Skype_Picture_2024_02_19T07_56_52_159Z.jpeg',
  );
  const contextValue = {
    role,
    setRole,
    email,
    setEmail,
    password,
    setPassword,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    bio,
    setBio,
    position,
    setPosition,
    experience,
    setExperience,
    rate,
    setRate,
    consultantGroups,
    setConsultantGroups,
    brandname,
    setBrandName,
    group,
    setGroup,
    country,
    setCountry,
    city,
    setCity,
    revenue,
    setRevenue,
    investment,
    setInvestment,
    company,
    setCompany,
    profilePhoto,
    setProfilePhoto,
    averageRevenue,
    setAverageRevenue,
    minInitialInvestment,
    setMinInitialInvestment,
    maxInitialInvestment,
    setMaxInitialInvestment,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

const useAuthContext = () => useContext(AuthContext);

export { AuthContextProvider, useAuthContext };
